import { Constants } from "@/integrations/supabase/types";
/**
 * Document types - Using Supabase helper types (official pattern)
 *
 * Note: Frontend uses string IDs for compatibility, while database uses numbers.
 * Use DocumentDB for database operations, Document for frontend state.
 */

import type { DocumentCategory } from "../domains/documents/services/documentService";
import type { Tables, TablesInsert, TablesUpdate } from "./supabase-helpers";

// Category type from document_categories table
export type DocumentCategoryRow = Tables<"document_categories">;

// Database types (auto-generated)
export type DocumentDB = Tables<"documents">;
export type DocumentInsert = TablesInsert<"documents">;
export type DocumentUpdate = TablesUpdate<"documents">;

// Document state enum (keep as manual type until added to database)
export type DocumentState =
  | NonNullable<DocumentDB["state"]>
  | "UNPROCESSED"
  | "CONTAINER"
  | "DWG_CONTAINER"
  | "DWG_CONVERTING"
  | "DWG_PROCESSED"
  | "DWG_FINALIZING"
  | "EXTRACTING"
  | "EXTRACTED"
  | "UNZIPPING"
  | "UNZIPPED"
  | "CLASSIFYING"
  | "CLASSIFIED"
  | "FOLDERING"
  | "FOLDERED"
  | "TAGGING"
  | "TAGGED"
  | "EXTRACTING_ROOMS"
  | "ROOMS_EXTRACTED"
  | "EMBEDDING"
  | "EMBEDDED"
  | "EXTRACTING_BUILDING"
  | "STAGING"
  | "STAGED"
  | "COMPLETE"
  | "PROCESSED"
  | "ERROR";

export interface Document {
  id: string; // Keep as string for frontend
  filename: string;
  file_path: string;
  building_id: string; // Keep as string for frontend
  parent_document_id?: string | null; // Keep as string for frontend
  content_type: string;
  size: number;
  created_at: string;
  category?: DocumentCategory;
  subcategory?: string; // For storing which subfolder this belongs to
  /** @deprecated Use categories instead. Kept for backward compatibility. */
  subcategories?: string[]; // Array of AI classifier tags from database
  /** Categories from M2M document_document_categories table (mapped to simplified format) */
  categories?: Array<{ id: number; name: string; name_et?: string | null }>;
  project_disciplines?: string[]; // Array of project discipline codes (enum)
  project_name?: string; // Project name for display as tag
  /** AI-populated design disciplines from junction table */
  design_disciplines?: Array<{
    id: number;
    name: string;
    name_en?: string | null;
    abbreviation: string | null;
    description?: string | null;
  }>;
  /** AI-populated design stages from junction table */
  design_stages?: Array<{
    id: number;
    name: string;
    name_en?: string | null;
    abbreviation: string | null;
  }>;
  body?: string;
  version?: number;
  version_group?: string; // To group different versions of the same file
  container_type?: string | null; // Routing tag ("dwg" | "ifc" | "zip") into a specialised pipeline
  content_hash?: string | null; // SHA256 hash for duplicate detection
  created_by?: string | number; // Person who created/uploaded
  created_by_name?: string; // Name of the person who uploaded
  created_by_avatar_url?: string | null; // Avatar (storage path or full URL)
  created_by_color?: string | null; // Stored avatar fill color (hex)
  verified_at?: string | null; // When this version was marked Verified (null = unverified)
  verified_by?: number | null; // user_profiles.id of the verifier
  verified_by_name?: string | null; // Verifier's display name, for the hover attribution
  modified_by?: string | number; // ID of the person who last modified
  modified_at?: string; // Timestamp of last modification
  modified_by_name?: string; // Name of the person who last modified
  org_id?: string; // Organization ID
  project_id?: number; // Project ID for document association
  original_created_at?: string; // Original creation date extracted from file metadata
  document_content_modified_date?: string; // Original modification date extracted from file metadata
  folder_tags?: string[]; // Array of folder tags for custom folder organization (legacy system)
  folder_id?: string; // Direct folder relationship (new hierarchical system)
  folder_path?: string; // Full folder path for display
  folder_name?: string; // Folder name for display
  childFolders?: Array<{
    id: string;
    name: string;
    category: "documents" | "drawings";
    parentId: string | null;
    parent_document_id?: string;
    isCustom?: boolean;
    building_id: number;
    project_id?: number;
    children: any[];
    documents?: Document[];
  }>; // Folders that have this document as parent_document_id
  tag_types?: {
    // New unified tag system
    system: string[];
    custom: string[];
  };
  createElement?: any;
  // Trash-related fields
  is_deleted?: boolean;
  deleted_at?: string;
  deleted_by?: string | number;
  deleted_by_name?: string;
  // Processing state fields
  state?: DocumentState;
  state_changed_at?: string;
  state_changed_by?: number;
}

// Composite types for frontend
export interface DocumentGroup {
  name: string;
  versions: Document[];
  latestVersion: Document;
  versionCount: number;
}

// Conversion helpers
export function toDocumentDB(
  doc: Partial<Document>,
  userId: number,
  orgId: string
): Partial<DocumentDB> {
  return {
    ...doc,
    id: doc.id ? parseInt(doc.id) : undefined,
    building_id: doc.building_id ? parseInt(doc.building_id, 10) : undefined,
    parent_document_id: doc.parent_document_id ? parseInt(doc.parent_document_id, 10) : undefined,
    created_by: userId,
    org_id: orgId,
    modified_by: doc.modified_by
      ? typeof doc.modified_by === "string"
        ? parseInt(doc.modified_by)
        : doc.modified_by
      : undefined,
    deleted_by: doc.deleted_by
      ? typeof doc.deleted_by === "string"
        ? parseInt(doc.deleted_by)
        : doc.deleted_by
      : undefined,
    project_id: doc.project_id,
    subcategories: enumValues(doc.subcategories, Constants.public.Enums.document_subcategory),
    project_disciplines: enumValues(
      doc.project_disciplines,
      Constants.public.Enums.project_discipline
    ),
    folder_id: doc.folder_id ? parseInt(doc.folder_id) : undefined,
  };
}

export function fromDocumentDB(
  doc: DocumentDB & {
    user_profiles?: {
      id?: number;
      first_name: string;
      last_name: string;
      avatar_url?: string | null;
      color?: string | null;
    };
    document_document_categories?: Array<{ document_categories: DocumentCategoryRow }>;
    document_design_disciplines?: Array<{
      design_disciplines: {
        id: number;
        name: string;
        name_en: string | null;
        abbreviation: string | null;
        description: string | null;
      };
    }>;
    document_design_stages?: Array<{
      design_stages: {
        id: number;
        name: string;
        name_en: string | null;
        abbreviation: string | null;
      };
    }>;
  }
): Document {
  // Extract categories from M2M relationship and map to expected format
  const categories =
    doc.document_document_categories
      ?.map((rel) => rel.document_categories)
      .filter((cat): cat is DocumentCategoryRow => cat != null)
      .map((cat) => ({
        id: cat.id,
        name: cat.category_name, // Map category_name → name
      })) || [];

  // Extract design disciplines from M2M relationship
  const design_disciplines =
    doc.document_design_disciplines
      ?.map((rel) => rel.design_disciplines)
      .filter(
        (
          disc
        ): disc is {
          id: number;
          name: string;
          name_en: string | null;
          abbreviation: string | null;
          description: string | null;
        } => disc != null
      )
      .map((disc) => ({
        id: disc.id,
        name: disc.name,
        name_en: disc.name_en,
        abbreviation: disc.abbreviation,
        description: disc.description,
      })) || [];

  // Extract design stages from M2M relationship
  const design_stages =
    doc.document_design_stages
      ?.map((rel) => rel.design_stages)
      .filter(
        (
          stage
        ): stage is {
          id: number;
          name: string;
          name_en: string | null;
          abbreviation: string | null;
        } => stage != null
      )
      .map((stage) => ({
        id: stage.id,
        name: stage.name,
        name_en: stage.name_en,
        abbreviation: stage.abbreviation,
      })) || [];

  // Extract category names for backward compatibility with subcategories
  const categoryNames = categories.map((cat) => cat.name);

  return {
    ...doc,
    id: String(doc.id),
    building_id: String(doc.building_id),
    parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
    created_by: String(doc.created_by),
    created_by_name: doc.user_profiles
      ? `${doc.user_profiles.first_name} ${doc.user_profiles.last_name}`
      : undefined,
    created_by_avatar_url: doc.user_profiles?.avatar_url ?? null,
    created_by_color: doc.user_profiles?.color ?? null,
    modified_by: doc.modified_by ? String(doc.modified_by) : undefined,
    deleted_by: doc.deleted_by ? String(doc.deleted_by) : undefined,
    project_id: doc.project_id,
    original_created_at: doc.original_created_at,
    // Use categories from M2M table, fallback to subcategories column for backward compatibility
    categories: categories.length > 0 ? categories : undefined,
    subcategories: categoryNames.length > 0 ? categoryNames : doc.subcategories,
    project_disciplines: doc.project_disciplines,
    // Design disciplines and stages from junction tables
    design_disciplines: design_disciplines.length > 0 ? design_disciplines : undefined,
    design_stages: design_stages.length > 0 ? design_stages : undefined,
    folder_id: doc.folder_id ? String(doc.folder_id) : undefined,
    version: doc.version,
    version_group: doc.version_group, // Explicitly map version_group to ensure it's preserved
    content_hash: doc.content_hash, // SHA256 hash for versioning
  };
}

function enumValues<T extends string>(
  values: string[] | undefined,
  allowed: readonly T[]
): T[] | undefined {
  return values?.map((value) => {
    const match = allowed.find((candidate) => candidate === value);
    if (match === undefined) throw new Error("Unknown document enum value: " + value);
    return match;
  });
}
